import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { lazy, Suspense, useEffect } from "react";
import Home from "./pages/Home";
import { useResumeCheckout } from "@/hooks/useResumeCheckout";
import { markNoSignupExemptionIfApplicable } from "@/lib/noSignupRoutes";
const Scan = lazy(() => import("./pages/Scan"));
const ScanDetail = lazy(() => import("./pages/ScanDetail"));
const History = lazy(() => import("./pages/History"));
const Library = lazy(() => import("./pages/Library"));
const Pricing = lazy(() => import("./pages/Pricing"));
const UseCases = lazy(() => import("./pages/UseCases"));
const NoSignupOCR = lazy(() => import("./pages/NoSignupOCR"));
const PdfToTextOcr = lazy(() => import("./pages/PdfToTextOcr"));
const OcrOnline = lazy(() => import("./pages/OcrOnline"));
const ExtractTextFromImage = lazy(() => import("./pages/ExtractTextFromImage"));
const PdfToTextConverter = lazy(() => import("./pages/PdfToTextConverter"));
const AiDocumentSummarizer = lazy(() => import("./pages/AiDocumentSummarizer"));
const DocumentScanner = lazy(() => import("./pages/DocumentScanner"));
const MicrosoftLensAlternative = lazy(() => import("./pages/MicrosoftLensAlternative"));
const ImageToTextConverter = lazy(() => import("./pages/ImageToTextConverter"));
const JpgToText = lazy(() => import("./pages/JpgToText"));
const JpgToTextConverter = lazy(() => import("./pages/JpgToTextConverter"));
const PngToText = lazy(() => import("./pages/PngToText"));
const ScreenshotToText = lazy(() => import("./pages/ScreenshotToText"));
const CopyTextFromImage = lazy(() => import("./pages/CopyTextFromImage"));
const PhotoToText = lazy(() => import("./pages/PhotoToText"));
const PictureToText = lazy(() => import("./pages/PictureToText"));
const ScanImageToText = lazy(() => import("./pages/ScanImageToText"));
const ReceiptToText = lazy(() => import("./pages/ReceiptToText"));
const InvoiceToText = lazy(() => import("./pages/InvoiceToText"));
const DocumentToText = lazy(() => import("./pages/DocumentToText"));
const HandwritingToText = lazy(() => import("./pages/HandwritingToText"));
const BusinessCardToText = lazy(() => import("./pages/BusinessCardToText"));
const ReadTextFromImage = lazy(() => import("./pages/ReadTextFromImage"));
const ExtractTextFromScreenshot = lazy(() => import("./pages/ExtractTextFromScreenshot"));
const CopyTextFromScreenshot = lazy(() => import("./pages/CopyTextFromScreenshot"));
const ReadTextFromScreenshot = lazy(() => import("./pages/ReadTextFromScreenshot"));
const ConvertImageToText = lazy(() => import("./pages/ConvertImageToText"));
const ConvertPhotoToText = lazy(() => import("./pages/ConvertPhotoToText"));
const ConvertPictureToText = lazy(() => import("./pages/ConvertPictureToText"));
const ConvertScreenshotToText = lazy(() => import("./pages/ConvertScreenshotToText"));
const ImageToTextOnline = lazy(() => import("./pages/ImageToTextOnline"));
const FreeImageToText = lazy(() => import("./pages/FreeImageToText"));
const ImageToTextFree = lazy(() => import("./pages/ImageToTextFree"));
const ImageToTextNoSignup = lazy(() => import("./pages/ImageToTextNoSignup"));
const ImageToTextEnglish = lazy(() => import("./pages/ImageToTextEnglish"));
const TranslateImageToText = lazy(() => import("./pages/TranslateImageToText"));
const TranslatePhotoToText = lazy(() => import("./pages/TranslatePhotoToText"));
const ImageTranslatorOnline = lazy(() => import("./pages/ImageTranslatorOnline"));
const TranslateScreenshotText = lazy(() => import("./pages/TranslateScreenshotText"));
const TranslateSpanishImageToEnglish = lazy(() => import("./pages/TranslateSpanishImageToEnglish"));
const TranslateImageEnglishToSpanish = lazy(() => import("./pages/TranslateImageEnglishToSpanish"));
const Tools = lazy(() => import("./pages/Tools"));
const ImageToPdf = lazy(() => import("./pages/ImageToPdf"));
const ExtractDataFromReceipts = lazy(() => import("./pages/ExtractDataFromReceipts"));
const ExtractDataFromInvoices = lazy(() => import("./pages/ExtractDataFromInvoices"));
const ExtractDataFromScreenshot = lazy(() => import("./pages/ExtractDataFromScreenshot"));
const ExtractReceiptTotal = lazy(() => import("./pages/ExtractReceiptTotal"));
const ExtractItemsFromReceipt = lazy(() => import("./pages/ExtractItemsFromReceipt"));
const ExtractTotalFromInvoice = lazy(() => import("./pages/ExtractTotalFromInvoice"));
const ExtractInvoiceTotal = lazy(() => import("./pages/ExtractInvoiceTotal"));
const ExtractTaxFromReceipt = lazy(() => import("./pages/ExtractTaxFromReceipt"));
const ExtractInvoiceData = lazy(() => import("./pages/ExtractInvoiceData"));
const ExtractInvoiceDataOnline = lazy(() => import("./pages/ExtractInvoiceDataOnline"));
const ReceiptDataExtractionAi = lazy(() => import("./pages/ReceiptDataExtractionAi"));
const AutomateReceiptProcessing = lazy(() => import("./pages/AutomateReceiptProcessing"));
const InvoiceDataExtractionSoftware = lazy(() => import("./pages/InvoiceDataExtractionSoftware"));
const ReceiptScannerForExpenses = lazy(() => import("./pages/ReceiptScannerForExpenses"));
const GoogleLensAlternative = lazy(() => import("./pages/GoogleLensAlternative"));
const VsCamScanner = lazy(() => import("./pages/VsCamScanner"));
const VsAdobeScan = lazy(() => import("./pages/VsAdobeScan"));
const VsAdobeAcrobatAndCamscanner = lazy(() => import("./pages/VsAdobeAcrobatAndCamscanner"));
const ScanHandwrittenNotesToText = lazy(() => import("./pages/ScanHandwrittenNotesToText"));
const ScanReceiptsAndExtractData = lazy(() => import("./pages/ScanReceiptsAndExtractData"));
const ScanReceipts = lazy(() => import("./pages/ScanReceipts"));
const ConvertHandwrittenNotesToText = lazy(() => import("./pages/ConvertHandwrittenNotesToText"));
const HandwrittenNotesToDigitalText = lazy(() => import("./pages/HandwrittenNotesToDigitalText"));
const ScannedPdfToText = lazy(() => import("./pages/ScannedPdfToText"));
const OcrPdfToText = lazy(() => import("./pages/OcrPdfToText"));
const PdfScanToText = lazy(() => import("./pages/PdfScanToText"));
const InvoiceOcr = lazy(() => import("./pages/InvoiceOcr"));
const ScanInvoiceToText = lazy(() => import("./pages/ScanInvoiceToText"));
const ReceiptOcr = lazy(() => import("./pages/ReceiptOcr"));
const ScanHandwritingOnline = lazy(() => import("./pages/ScanHandwritingOnline"));
const ScanDocumentToText = lazy(() => import("./pages/ScanDocumentToText"));
const RecognizeHandwrittenNotes = lazy(() => import("./pages/RecognizeHandwrittenNotes"));
const AiDocumentProcessing = lazy(() => import("./pages/AiDocumentProcessing"));
const ExtractInvoiceDataFromPdf = lazy(() => import("./pages/ExtractInvoiceDataFromPdf"));
const InvoiceProcessingAutomation = lazy(() => import("./pages/InvoiceProcessingAutomation"));
const DocumentProcessingAutomation = lazy(() => import("./pages/DocumentProcessingAutomation"));
const IntelligentDocumentProcessing = lazy(() => import("./pages/IntelligentDocumentProcessing"));
const IntelligentDocumentRecognition = lazy(() => import("./pages/IntelligentDocumentRecognition"));
const DataExtractionSoftware = lazy(() => import("./pages/DataExtractionSoftware"));
const AutomatedDataExtraction = lazy(() => import("./pages/AutomatedDataExtraction"));
const DocumentDataExtraction = lazy(() => import("./pages/DocumentDataExtraction"));
const AiDataExtraction = lazy(() => import("./pages/AiDataExtraction"));
const DocumentWorkflowAutomation = lazy(() => import("./pages/DocumentWorkflowAutomation"));
const PdfDataExtraction = lazy(() => import("./pages/PdfDataExtraction"));
const AiPdfDataExtraction = lazy(() => import("./pages/AiPdfDataExtraction"));
const ExtractDataFromPdf = lazy(() => import("./pages/ExtractDataFromPdf"));
const PdfTableExtraction = lazy(() => import("./pages/PdfTableExtraction"));
const InvoiceAutomationSoftware = lazy(() => import("./pages/InvoiceAutomationSoftware"));
const AutomateInvoiceProcessing = lazy(() => import("./pages/AutomateInvoiceProcessing"));
const HandwritingOcrOnline = lazy(() => import("./pages/HandwritingOcrOnline"));
const FreeOcrNoSignup = lazy(() => import("./pages/FreeOcrNoSignup"));
const JpgOcrOnline = lazy(() => import("./pages/JpgOcrOnline"));
const HandwrittenNotesOcr = lazy(() => import("./pages/HandwrittenNotesOcr"));
const CursiveHandwritingOcr = lazy(() => import("./pages/CursiveHandwritingOcr"));
const PdfOcrNoLogin = lazy(() => import("./pages/PdfOcrNoLogin"));
const FreeJpgToTextOnline = lazy(() => import("./pages/FreeJpgToTextOnline"));
const TranslateImageToEnglish = lazy(() => import("./pages/TranslateImageToEnglish"));
const InvoiceOcrSoftware = lazy(() => import("./pages/InvoiceOcrSoftware"));
const TranslateFrenchImageToEnglish = lazy(() => import("./pages/TranslateFrenchImageToEnglish"));
const PdfInvoiceParser = lazy(() => import("./pages/PdfInvoiceParser"));
const InvoiceToExcel = lazy(() => import("./pages/InvoiceToExcel"));
const PdfToExcelAi = lazy(() => import("./pages/PdfToExcelAi"));
const ReceiptToExcel = lazy(() => import("./pages/ReceiptToExcel"));
const BankStatementToExcel = lazy(() => import("./pages/BankStatementToExcel"));
const ConvertPdfBankStatementToExcel = lazy(() => import("./pages/ConvertPdfBankStatementToExcel"));
const BankStatementConverter = lazy(() => import("./pages/BankStatementConverter"));
const BankStatementToCsv = lazy(() => import("./pages/BankStatementToCsv"));
const PdfToCsv = lazy(() => import("./pages/PdfToCsv"));
const TableFromPdfToExcel = lazy(() => import("./pages/TableFromPdfToExcel"));
const ConvertScannedContractToWord = lazy(() => import("./pages/ConvertScannedContractToWord"));
const LegalDocumentOcr = lazy(() => import("./pages/LegalDocumentOcr"));
const ConvertNdaToEditableText = lazy(() => import("./pages/ConvertNdaToEditableText"));
const ExtractContractTextFromPdf = lazy(() => import("./pages/ExtractContractTextFromPdf"));
const PdfContractToWord = lazy(() => import("./pages/PdfContractToWord"));
const PdfToWordOcr = lazy(() => import("./pages/PdfToWordOcr"));
const PdfToDocxConverter = lazy(() => import("./pages/PdfToDocxConverter"));
const PdfToGoogleDocs = lazy(() => import("./pages/PdfToGoogleDocs"));
const BankStatementToExcelAccountingGuide = lazy(() => import("./pages/BankStatementToExcelAccountingGuide"));
const ConvertScannedPdfToWordEditable = lazy(() => import("./pages/ConvertScannedPdfToWordEditable"));
const ConvertPdfContractToWord = lazy(() => import("./pages/ConvertPdfContractToWord"));
const EditScannedPdfDocument = lazy(() => import("./pages/EditScannedPdfDocument"));
const ConvertPdfFormToEditableWord = lazy(() => import("./pages/ConvertPdfFormToEditableWord"));
const EditScannedPdfOnline = lazy(() => import("./pages/EditScannedPdfOnline"));
const EditPdfWithoutAdobe = lazy(() => import("./pages/EditPdfWithoutAdobe"));
const EditContractPdfOnline = lazy(() => import("./pages/EditContractPdfOnline"));
const MakeContractPdfSearchableOnline = lazy(() => import("./pages/MakeContractPdfSearchableOnline"));
const ExtractTextFromScannedAgreement = lazy(() => import("./pages/ExtractTextFromScannedAgreement"));
const FindClausesInContract = lazy(() => import("./pages/FindClausesInContract"));
const EditNdaDocumentOnline = lazy(() => import("./pages/EditNdaDocumentOnline"));
const EditScannedContractOnline = lazy(() => import("./pages/EditScannedContractOnline"));
const ConvertSignedContractToWord = lazy(() => import("./pages/ConvertSignedContractToWord"));
const ExtractClausesFromContract = lazy(() => import("./pages/ExtractClausesFromContract"));
const ScanLeaseAgreement = lazy(() => import("./pages/ScanLeaseAgreement"));
const ConvertInvoicePdfToExcel = lazy(() => import("./pages/ConvertInvoicePdfToExcel"));
const ExtractInvoiceLineItems = lazy(() => import("./pages/ExtractInvoiceLineItems"));
const SearchScannedContract = lazy(() => import("./pages/SearchScannedContract"));
const FindTermsInContract = lazy(() => import("./pages/FindTermsInContract"));
const ExtractBankStatementTransactions = lazy(() => import("./pages/ExtractBankStatementTransactions"));
const EditScannedFormOnline = lazy(() => import("./pages/EditScannedFormOnline"));
const FillablePdfToWord = lazy(() => import("./pages/FillablePdfToWord"));
const ExtractPurchaseOrderData = lazy(() => import("./pages/ExtractPurchaseOrderData"));
const ConvertPurchaseOrderToExcel = lazy(() => import("./pages/ConvertPurchaseOrderToExcel"));
const FillPdfFormOnline = lazy(() => import("./pages/FillPdfFormOnline"));
const EditablePdfForm = lazy(() => import("./pages/EditablePdfForm"));
const ExtractExpenseDataFromReceipts = lazy(() => import("./pages/ExtractExpenseDataFromReceipts"));
const ExtractDataFromForm = lazy(() => import("./pages/ExtractDataFromForm"));
const ConvertResumePdfToWord = lazy(() => import("./pages/ConvertResumePdfToWord"));
const ConvertReportPdfToExcel = lazy(() => import("./pages/ConvertReportPdfToExcel"));
const EditScannedApplication = lazy(() => import("./pages/EditScannedApplication"));
const Auth = lazy(() => import("./pages/Auth"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GaTest = lazy(() => import("./pages/GaTest"));
const About = lazy(() => import("./pages/About"));
const ResearchOcrAccuracyBenchmark = lazy(() => import("./pages/ResearchOcrAccuracyBenchmark"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
















































































































const queryClient = new QueryClient();

const ResumeCheckoutMount = () => {
  useResumeCheckout();
  return null;
};

// Marks the tab session exempt from the signup gate whenever the visitor lands
// on a route with explicit "no signup / no registration" search intent.
const NoSignupExemptionMount = () => {
  const location = useLocation();
  useEffect(() => {
    markNoSignupExemptionIfApplicable(location.pathname);
  }, [location.pathname]);
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ResumeCheckoutMount />
          <NoSignupExemptionMount />
          <Suspense fallback={null}><Routes>
            <Route path="/" element={<Home />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/scan/:id" element={<ScanDetail />} />
            <Route path="/history" element={<History />} />
            <Route path="/library" element={<Library />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/no-signup-ocr" element={<NoSignupOCR />} />
            <Route path="/pdf-to-text-ocr" element={<PdfToTextOcr />} />
            <Route path="/ocr-online" element={<OcrOnline />} />
            <Route path="/extract-text-from-image" element={<ExtractTextFromImage />} />
            <Route path="/pdf-to-text-converter" element={<PdfToTextConverter />} />
            <Route path="/ai-document-summarizer" element={<AiDocumentSummarizer />} />
            <Route path="/document-scanner" element={<DocumentScanner />} />
            <Route path="/microsoft-lens-alternative" element={<MicrosoftLensAlternative />} />
            <Route path="/image-to-text-converter" element={<ImageToTextConverter />} />
            <Route path="/jpg-to-text" element={<JpgToText />} />
            <Route path="/jpg-to-text-converter" element={<JpgToTextConverter />} />
            <Route path="/png-to-text" element={<PngToText />} />
            <Route path="/screenshot-to-text" element={<ScreenshotToText />} />
            <Route path="/copy-text-from-image" element={<CopyTextFromImage />} />
            <Route path="/photo-to-text" element={<PhotoToText />} />
            <Route path="/picture-to-text" element={<PictureToText />} />
            <Route path="/scan-image-to-text" element={<ScanImageToText />} />
            <Route path="/receipt-to-text" element={<ReceiptToText />} />
            <Route path="/invoice-to-text" element={<InvoiceToText />} />
            <Route path="/document-to-text" element={<DocumentToText />} />
            <Route path="/handwriting-to-text" element={<HandwritingToText />} />
            <Route path="/business-card-to-text" element={<BusinessCardToText />} />
            <Route path="/read-text-from-image" element={<ReadTextFromImage />} />
            <Route path="/extract-text-from-screenshot" element={<ExtractTextFromScreenshot />} />
            <Route path="/copy-text-from-screenshot" element={<CopyTextFromScreenshot />} />
            <Route path="/read-text-from-screenshot" element={<ReadTextFromScreenshot />} />
            <Route path="/convert-image-to-text" element={<ConvertImageToText />} />
            <Route path="/convert-photo-to-text" element={<ConvertPhotoToText />} />
            <Route path="/convert-picture-to-text" element={<ConvertPictureToText />} />
            <Route path="/convert-screenshot-to-text" element={<ConvertScreenshotToText />} />
            <Route path="/image-to-text-online" element={<ImageToTextOnline />} />
            <Route path="/free-image-to-text" element={<FreeImageToText />} />
            <Route path="/image-to-text-free" element={<ImageToTextFree />} />
            <Route path="/image-to-text-no-signup" element={<ImageToTextNoSignup />} />
            <Route path="/image-to-text-english" element={<ImageToTextEnglish />} />
            <Route path="/translate-image-to-text" element={<TranslateImageToText />} />
            <Route path="/translate-photo-to-text" element={<TranslatePhotoToText />} />
            <Route path="/image-translator-online" element={<ImageTranslatorOnline />} />
            <Route path="/translate-screenshot-text" element={<TranslateScreenshotText />} />
            <Route path="/translate-spanish-image-to-english" element={<TranslateSpanishImageToEnglish />} />
            <Route path="/translate-image-english-to-spanish" element={<TranslateImageEnglishToSpanish />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/image-to-pdf" element={<ImageToPdf />} />
            <Route path="/extract-data-from-receipts" element={<ExtractDataFromReceipts />} />
            <Route path="/extract-data-from-invoices" element={<ExtractDataFromInvoices />} />
            <Route path="/extract-data-from-screenshot" element={<ExtractDataFromScreenshot />} />
            <Route path="/extract-receipt-total" element={<ExtractReceiptTotal />} />
            <Route path="/extract-items-from-receipt" element={<ExtractItemsFromReceipt />} />
            <Route path="/extract-total-from-invoice" element={<ExtractTotalFromInvoice />} />
            <Route path="/extract-invoice-total" element={<ExtractInvoiceTotal />} />
            <Route path="/extract-tax-from-receipt" element={<ExtractTaxFromReceipt />} />
            <Route path="/extract-invoice-data" element={<ExtractInvoiceData />} />
            <Route path="/extract-invoice-data-online" element={<ExtractInvoiceDataOnline />} />
            <Route path="/receipt-data-extraction-ai" element={<ReceiptDataExtractionAi />} />
            <Route path="/automate-receipt-processing" element={<AutomateReceiptProcessing />} />
            <Route path="/invoice-data-extraction-software" element={<InvoiceDataExtractionSoftware />} />
            <Route path="/receipt-scanner-for-expenses" element={<ReceiptScannerForExpenses />} />
            <Route path="/google-lens-alternative" element={<GoogleLensAlternative />} />
            <Route path="/vs-camscanner" element={<VsCamScanner />} />
            <Route path="/vs-adobe-scan" element={<VsAdobeScan />} />
            <Route path="/vs-adobe-acrobat-and-camscanner" element={<VsAdobeAcrobatAndCamscanner />} />
            <Route path="/scan-handwritten-notes-to-text" element={<ScanHandwrittenNotesToText />} />
            <Route path="/scan-receipts-and-extract-data" element={<ScanReceiptsAndExtractData />} />
            <Route path="/convert-handwritten-notes-to-text" element={<ConvertHandwrittenNotesToText />} />
            <Route path="/scan-receipts" element={<ScanReceipts />} />
            <Route path="/handwritten-notes-to-digital-text" element={<HandwrittenNotesToDigitalText />} />
            <Route path="/scanned-pdf-to-text" element={<ScannedPdfToText />} />
            <Route path="/ocr-pdf-to-text" element={<OcrPdfToText />} />
            <Route path="/pdf-scan-to-text" element={<PdfScanToText />} />
            <Route path="/invoice-ocr" element={<InvoiceOcr />} />
            <Route path="/scan-invoice-to-text" element={<ScanInvoiceToText />} />
            <Route path="/receipt-ocr" element={<ReceiptOcr />} />
            <Route path="/scan-handwriting-online" element={<ScanHandwritingOnline />} />
            <Route path="/scan-document-to-text" element={<ScanDocumentToText />} />
            <Route path="/recognize-handwritten-notes" element={<RecognizeHandwrittenNotes />} />
            <Route path="/ai-document-processing" element={<AiDocumentProcessing />} />
            <Route path="/extract-invoice-data-from-pdf" element={<ExtractInvoiceDataFromPdf />} />
            <Route path="/invoice-processing-automation" element={<InvoiceProcessingAutomation />} />
            <Route path="/document-processing-automation" element={<DocumentProcessingAutomation />} />
            <Route path="/intelligent-document-processing" element={<IntelligentDocumentProcessing />} />
            <Route path="/intelligent-document-recognition" element={<IntelligentDocumentRecognition />} />
            <Route path="/data-extraction-software" element={<DataExtractionSoftware />} />
            <Route path="/automated-data-extraction" element={<AutomatedDataExtraction />} />
            <Route path="/document-data-extraction" element={<DocumentDataExtraction />} />
            <Route path="/ai-data-extraction" element={<AiDataExtraction />} />
            <Route path="/document-workflow-automation" element={<DocumentWorkflowAutomation />} />
            <Route path="/pdf-data-extraction" element={<PdfDataExtraction />} />
            <Route path="/ai-pdf-data-extraction" element={<AiPdfDataExtraction />} />
            <Route path="/extract-data-from-pdf" element={<ExtractDataFromPdf />} />
            <Route path="/pdf-table-extraction" element={<PdfTableExtraction />} />
            <Route path="/invoice-automation-software" element={<InvoiceAutomationSoftware />} />
            <Route path="/automate-invoice-processing" element={<AutomateInvoiceProcessing />} />
            <Route path="/handwriting-ocr-online" element={<HandwritingOcrOnline />} />
            <Route path="/free-ocr-no-signup" element={<FreeOcrNoSignup />} />
            <Route path="/jpg-ocr-online" element={<JpgOcrOnline />} />
            <Route path="/handwritten-notes-ocr" element={<HandwrittenNotesOcr />} />
            <Route path="/cursive-handwriting-ocr" element={<CursiveHandwritingOcr />} />
            <Route path="/pdf-ocr-no-login" element={<PdfOcrNoLogin />} />
            <Route path="/free-jpg-to-text-online" element={<FreeJpgToTextOnline />} />
            <Route path="/translate-image-to-english" element={<TranslateImageToEnglish />} />
            <Route path="/invoice-ocr-software" element={<InvoiceOcrSoftware />} />
            <Route path="/translate-french-image-to-english" element={<TranslateFrenchImageToEnglish />} />
            <Route path="/pdf-invoice-parser" element={<PdfInvoiceParser />} />
            <Route path="/invoice-to-excel" element={<InvoiceToExcel />} />
            <Route path="/pdf-to-excel-ai" element={<PdfToExcelAi />} />
            <Route path="/receipt-to-excel" element={<ReceiptToExcel />} />
            <Route path="/bank-statement-to-excel" element={<BankStatementToExcel />} />
            <Route path="/convert-pdf-bank-statement-to-excel" element={<ConvertPdfBankStatementToExcel />} />
            <Route path="/bank-statement-converter" element={<BankStatementConverter />} />
            <Route path="/bank-statement-to-csv" element={<BankStatementToCsv />} />
            <Route path="/pdf-to-csv" element={<PdfToCsv />} />
            <Route path="/table-from-pdf-to-excel" element={<TableFromPdfToExcel />} />
            <Route path="/convert-scanned-contract-to-word" element={<ConvertScannedContractToWord />} />
            <Route path="/legal-document-ocr" element={<LegalDocumentOcr />} />
            <Route path="/convert-nda-to-editable-text" element={<ConvertNdaToEditableText />} />
            <Route path="/extract-contract-text-from-pdf" element={<ExtractContractTextFromPdf />} />
            <Route path="/pdf-contract-to-word" element={<PdfContractToWord />} />
            <Route path="/pdf-to-word-ocr" element={<PdfToWordOcr />} />
            <Route path="/pdf-to-docx-converter" element={<PdfToDocxConverter />} />
            <Route path="/convert-scanned-pdf-to-word-editable" element={<ConvertScannedPdfToWordEditable />} />
            <Route path="/convert-pdf-contract-to-word" element={<ConvertPdfContractToWord />} />
            <Route path="/edit-scanned-pdf-document" element={<EditScannedPdfDocument />} />
            <Route path="/convert-pdf-form-to-editable-word" element={<ConvertPdfFormToEditableWord />} />
            <Route path="/edit-scanned-pdf-online" element={<EditScannedPdfOnline />} />
            <Route path="/edit-pdf-without-adobe" element={<EditPdfWithoutAdobe />} />
            <Route path="/edit-contract-pdf-online" element={<EditContractPdfOnline />} />
            <Route path="/make-contract-pdf-searchable-online" element={<MakeContractPdfSearchableOnline />} />
            <Route path="/extract-text-from-scanned-agreement" element={<ExtractTextFromScannedAgreement />} />
            <Route path="/find-clauses-in-contract" element={<FindClausesInContract />} />
            <Route path="/edit-nda-document-online" element={<EditNdaDocumentOnline />} />
            <Route path="/edit-scanned-contract-online" element={<EditScannedContractOnline />} />
            <Route path="/convert-signed-contract-to-word" element={<ConvertSignedContractToWord />} />
            <Route path="/extract-clauses-from-contract" element={<ExtractClausesFromContract />} />
            <Route path="/scan-lease-agreement" element={<ScanLeaseAgreement />} />
            <Route path="/convert-invoice-pdf-to-excel" element={<ConvertInvoicePdfToExcel />} />
            <Route path="/extract-invoice-line-items" element={<ExtractInvoiceLineItems />} />
            <Route path="/search-scanned-contract" element={<SearchScannedContract />} />
            <Route path="/find-terms-in-contract" element={<FindTermsInContract />} />
            <Route path="/extract-bank-statement-transactions" element={<ExtractBankStatementTransactions />} />
            <Route path="/edit-scanned-form-online" element={<EditScannedFormOnline />} />
            <Route path="/fillable-pdf-to-word" element={<FillablePdfToWord />} />
            <Route path="/extract-purchase-order-data" element={<ExtractPurchaseOrderData />} />
            <Route path="/convert-purchase-order-to-excel" element={<ConvertPurchaseOrderToExcel />} />
            <Route path="/fill-pdf-form-online" element={<FillPdfFormOnline />} />
            <Route path="/editable-pdf-form" element={<EditablePdfForm />} />
            <Route path="/extract-expense-data-from-receipts" element={<ExtractExpenseDataFromReceipts />} />
            <Route path="/extract-data-from-form" element={<ExtractDataFromForm />} />
            <Route path="/convert-resume-pdf-to-word" element={<ConvertResumePdfToWord />} />
            <Route path="/convert-report-pdf-to-excel" element={<ConvertReportPdfToExcel />} />
            <Route path="/edit-scanned-application" element={<EditScannedApplication />} />
            <Route path="/pdf-to-google-docs" element={<PdfToGoogleDocs />} />
            <Route path="/guides/bank-statement-to-excel-accounting-guide" element={<BankStatementToExcelAccountingGuide />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/research/ocr-accuracy-benchmark" element={<ResearchOcrAccuracyBenchmark />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            <Route path="/ga-test" element={<GaTest />} />
            <Route path="*" element={<NotFound />} />
          </Routes></Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;